import { Configuration } from "./api/axios-client";
import axios from 'axios';
import { beginGlobalLoadingMask, endGlobalLoadingMask } from "../Data/Atoms";

type ApiServiceConstructor<T> = new (configuration: Configuration) => T;

const getAuthToken = (): string | null => {
  let authToken = localStorage.getItem('authToken');
  if (authToken) {
    const jAuthToken = JSON.parse(authToken) as any;
    if (jAuthToken['accessToken']) {
      return jAuthToken['accessToken'];
    } else {
      return null;
    }
  }
  return null;
};

const getCustomHeaders = (): string => {
  const hostname = window.location.hostname;
  const subdomain = hostname.split('.')[0];
  return subdomain;
};

export const checkExpiration = (token: string): boolean => {
  const payload = JSON.parse(atob(token.split('.')[1]));
  const expirationTime = payload.exp * 1000;
  const isExpired = Date.now() > expirationTime;
  return isExpired;
};

// Ensure we have a stable per-device identifier required by the auth API
export const getOrCreateDeviceId = (): string => {
  try {
    const key = 'deviceId';
    let id = localStorage.getItem(key);
    if (!id) {
      if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
        id = (crypto as any).randomUUID();
      } else {
        id = Math.random().toString(36).slice(2) + Date.now().toString(36);
      }
      localStorage.setItem(key, id!);
    }
    return id!;
  } catch {
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
  }
};

// Function to refresh the token to match backend contract (cookie-based RT, device-bound)
const refreshAuthToken = async (accessToken: string): Promise<string | null> => {
  try {
    const deviceId = getOrCreateDeviceId();
    const response = await axios.post(
      `${import.meta.env.VITE_SPOT_SYNC_SERVICES}/auth/refresh`,
      {
          accessToken: accessToken,
         deviceId: deviceId,
         refreshToken: ''
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'X-Customer-Subdomain': getCustomHeaders(),
        },
        withCredentials: true, // send/receive HttpOnly refresh token cookie
      }
    );

    const { accessToken: newAccessToken } = response.data || {};
    if (newAccessToken) {
      localStorage.setItem('authToken', JSON.stringify({ accessToken: newAccessToken }));
      return newAccessToken;
    }
    return null;
  } catch (error) {
    console.error('Refresh token failed:', error);
    return null;
  }
};

export const axiosClient = axios.create({
  baseURL: import.meta.env.VITE_SPOT_SYNC_SERVICES,
  headers: {
    'Content-Type': 'application/json',
    'X-Customer-Subdomain': getCustomHeaders(),
  },
});

axiosClient.interceptors.request.use(async (config) => {
   // start global loading mask
   try {
    beginGlobalLoadingMask();
  } catch {}
  const token = getAuthToken();

  if (token) {
    const isExpired = checkExpiration(token);

    if (isExpired) {
      // If the token is expired, try to refresh it using device-bound cookie flow
      const newToken = await refreshAuthToken(token);

      if (newToken) {
        // Set the new access token in the headers
        config.headers.Authorization = `Bearer ${newToken}`;
      } else {
        // If we couldn't refresh the token, redirect to login
        localStorage.removeItem('authToken');
        window.location.href = '/login';
        return Promise.reject('Token refresh failed, redirecting to login');
      }
    } else {
      // Add the token to the request header if it's still valid
      config.headers.Authorization = `Bearer ${token}`;
    }
  }

  return config;
}, (error) => {
  return Promise.reject(error);
});

axiosClient.interceptors.response.use(
  (response) => {
    try { endGlobalLoadingMask(); } catch {}
    return response;
  },
  (error) => {
    try { endGlobalLoadingMask(); } catch {}
    return Promise.reject(error);
  }
);


// API Service creation logic
export const createApiService = <T>(ApiClass: ApiServiceConstructor<T>): T => {
  const apiClient: any = new ApiClass(
    new Configuration({
      basePath: import.meta.env.VITE_SPOT_SYNC_SERVICES,
    })
  );
  apiClient['axios'] = axiosClient;
  return apiClient;
};